import { useState, useRef, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { usePageTitle } from "@/hooks/use-page-title";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useLocation } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { Building2, DollarSign, Lock, Check, ArrowRight, ArrowLeft } from "lucide-react";
import healthWalletLogo from "@assets/generated_images/healthwallet_shield_heart_logo.png";
import { LanguageSwitcher } from "@/components/language-switcher";
import { useTranslation, Trans } from "react-i18next";
import { supportedLanguages, type SupportedLanguage } from "@/i18n/config";

function createSignupSchema(t: (key: string) => string) {
  return z.object({
    companyName: z.string().min(1, t('sb.zodCompanyNameRequired')),
    contactName: z.string().min(1, t('sb.zodContactNameRequired')),
    contactEmail: z.string().email(t('sb.zodEmailInvalid')),
    contactPhone: z.string().optional(),
    industry: z.string().min(1, t('sb.zodIndustryRequired')),
    companySize: z.string().min(1, t('sb.zodCompanySizeRequired')),
    address: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zipCode: z.string().optional(),
    contributionModel: z.string().min(1, t('sb.zodContributionModelRequired')),
    monthlyPerEmployee: z.coerce.number().min(1, t('sb.zodMonthlyAmountMin')),
    matchPercent: z.coerce.number().min(0).max(100).optional(),
    password: z.string()
      .min(8, t('sb.zodPasswordMin'))
      .regex(/[A-Z]/, t('sb.zodPasswordUppercase'))
      .regex(/[0-9]/, t('sb.zodPasswordNumber')),
    confirmPassword: z.string().min(8, t('sb.zodConfirmPasswordRequired')),
    termsAgreed: z.boolean().refine((val) => val === true, t('sb.zodTermsRequired')),
  }).refine((data) => data.password === data.confirmPassword, {
    message: t('sb.zodPasswordsMismatch'),
    path: ["confirmPassword"],
  });
}

type SignupFormValues = {
  companyName: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  industry: string;
  companySize: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  contributionModel: string;
  monthlyPerEmployee: number;
  matchPercent?: number;
  password: string;
  confirmPassword: string;
  termsAgreed: boolean;
};

const industryKeys = [
  { value: "restaurant", labelKey: "sb.industryRestaurant" },
  { value: "retail", labelKey: "sb.industryRetail" },
  { value: "construction", labelKey: "sb.industryConstruction" },
  { value: "landscaping", labelKey: "sb.industryLandscaping" },
  { value: "cleaning", labelKey: "sb.industryCleaning" },
  { value: "auto_repair", labelKey: "sb.industryAutoRepair" },
  { value: "salon_barbershop", labelKey: "sb.industrySalonBarbershop" },
  { value: "childcare", labelKey: "sb.industryChildcare" },
  { value: "other", labelKey: "sb.industryOther" },
];

const companySizeKeys = [
  { value: "1-5", labelKey: "sb.size1to5" },
  { value: "6-10", labelKey: "sb.size6to10" },
  { value: "11-25", labelKey: "sb.size11to25" },
  { value: "26-50", labelKey: "sb.size26to50" },
  { value: "51-100", labelKey: "sb.size51to100" },
];

const contributionModelKeys = [
  { value: "fixed", labelKey: "sb.modelFixed", descKey: "sb.modelFixedDesc" },
  { value: "match", labelKey: "sb.modelMatch", descKey: "sb.modelMatchDesc" },
  { value: "emergency", labelKey: "sb.modelEmergency", descKey: "sb.modelEmergencyDesc" },
];

const steps = [
  { number: 1, labelKey: "sb.step1Label", icon: Building2 },
  { number: 2, labelKey: "sb.step2Label", icon: DollarSign },
  { number: 3, labelKey: "sb.step3Label", icon: Lock },
];

function getEmployeeCount(size: string): number {
  const midpoints: Record<string, number> = {
    "1-5": 3,
    "6-10": 8,
    "11-25": 18,
    "26-50": 38,
    "51-100": 75,
  };
  return midpoints[size] || 5;
}

function StepIndicator({ currentStep }: { currentStep: number }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center gap-0 mb-8" data-testid="step-indicator">
      {steps.map((step, index) => {
        const isCompleted = currentStep > step.number;
        const isActive = currentStep === step.number;
        const StepIcon = step.icon;

        return (
          <div key={step.number} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${
                  isCompleted
                    ? "bg-primary border-primary text-primary-foreground"
                    : isActive
                    ? "border-primary text-primary bg-primary/10"
                    : "border-muted-foreground/30 text-muted-foreground"
                }`}
                data-testid={`step-circle-${step.number}`}
              >
                {isCompleted ? <Check className="w-5 h-5" /> : <StepIcon className="w-5 h-5" />}
              </div>
              <span
                className={`text-xs mt-1.5 font-medium ${
                  isActive ? "text-primary" : isCompleted ? "text-foreground" : "text-muted-foreground"
                }`}
                data-testid={`step-label-${step.number}`}
              >
                {t(step.labelKey)}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`w-16 sm:w-24 h-0.5 mx-2 mb-5 ${
                  currentStep > step.number ? "bg-primary" : "bg-muted-foreground/20"
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function SmallBusinessSignupPage() {
  usePageTitle("Small Business Health Benefits Signup", "Sign up your small business for HealthWallet employer health benefits in the Twin Cities area.");
  const { t, i18n } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const [currentStep, setCurrentStep] = useState(1);
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const form = useForm<SignupFormValues>({
    mode: 'onBlur',
    resolver: (...args) => zodResolver(createSignupSchema(tRef.current))(...args),
    defaultValues: {
      companyName: "",
      contactName: "",
      contactEmail: "",
      contactPhone: "",
      industry: "",
      companySize: "",
      address: "",
      city: "",
      state: "MN",
      zipCode: "",
      contributionModel: "",
      monthlyPerEmployee: 50,
      matchPercent: 50,
      password: "",
      confirmPassword: "",
      termsAgreed: false,
    },
  });

  const signupMutation = useMutation({
    mutationFn: async (data: SignupFormValues) => {
      const response = await apiRequest("POST", "/api/small-business/signup", data);
      return response.json();
    },
    onSuccess: (data: any) => {
      if (data.user) {
        queryClient.setQueryData(["/api/user"], data.user);
        toast({
          title: t('sb.toastSuccessTitle'),
          description: t('sb.toastSuccessDesc'),
        });
        setLocation("/small-business/dashboard");
      } else {
        toast({
          title: t('sb.toastSuccessTitle'),
          description: data.message || t('sb.toastSuccessDescAlt'),
        });
        setLocation("/auth");
      }
    },
    onError: (error: any) => {
      toast({
        title: t('sb.toastErrorTitle'),
        description: error.message || t('sb.toastErrorDesc'),
        variant: "destructive",
      });
    },
  });

  const validateStep = async (step: number): Promise<boolean> => {
    let fieldsToValidate: (keyof SignupFormValues)[] = [];
    if (step === 1) {
      fieldsToValidate = ["companyName", "contactName", "contactEmail", "industry", "companySize"];
    } else if (step === 2) {
      fieldsToValidate = ["contributionModel", "monthlyPerEmployee"];
      if (form.getValues("contributionModel") === "match") {
        fieldsToValidate.push("matchPercent");
      }
    } else if (step === 3) {
      fieldsToValidate = ["password", "confirmPassword", "termsAgreed"];
    }
    const result = await form.trigger(fieldsToValidate);
    return result;
  };

  useEffect(() => {
    const errorFields = Object.keys(form.formState.errors) as (keyof SignupFormValues)[];
    if (errorFields.length > 0) {
      form.trigger(errorFields);
    }
  }, [i18n.language]);

  const handleNext = async () => {
    const isValid = await validateStep(currentStep);
    if (isValid) {
      setCurrentStep((prev) => Math.min(prev + 1, 3));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = form.handleSubmit((data) => {
    signupMutation.mutate(data);
  });

  const contributionModel = form.watch("contributionModel");
  const monthlyPerEmployee = form.watch("monthlyPerEmployee");
  const companySize = form.watch("companySize");
  const employeeCount = getEmployeeCount(companySize);
  const estimatedCost = employeeCount * (monthlyPerEmployee || 0);

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="border-b bg-background">
        <div className="max-w-2xl mx-auto px-4 py-4 sm:py-6 flex items-center justify-between gap-2 sm:gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img src={healthWalletLogo} alt="HealthWallet" className="h-8 w-8 sm:h-10 sm:w-10 object-contain shrink-0" width="1024" height="1024" data-testid="img-logo" />
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-bold leading-tight truncate" data-testid="text-page-title">{t('sb.pageTitle')}</h1>
              <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">{t('sb.pageSubtitle')}</p>
            </div>
          </div>
          <div className="shrink-0">
            <LanguageSwitcher />
          </div>
        </div>
        <div className="sm:hidden border-t bg-muted/30" data-testid="mobile-language-bar">
          <div className="max-w-2xl mx-auto px-4 py-2 flex gap-2">
            {supportedLanguages.map((lang) => (
              <button
                key={lang.code}
                type="button"
                onClick={() => {
                  i18n.changeLanguage(lang.code as SupportedLanguage);
                  localStorage.setItem('healthwallet-language', lang.code);
                }}
                data-testid={`mobile-lang-${lang.code}`}
                className={`flex-1 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                  i18n.resolvedLanguage === lang.code
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-border hover-elevate'
                }`}
              >
                {lang.nativeName}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <StepIndicator currentStep={currentStep} />

        <Form {...form}>
          <form onSubmit={handleSubmit}>
            {currentStep === 1 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    {t('sb.step1CardTitle')}
                  </CardTitle>
                  <CardDescription>{t('sb.step1CardDesc')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="companyName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('sb.labelCompanyName')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('sb.placeholderCompanyName')} {...field} data-testid="input-company-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="contactName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('sb.labelContactName')}</FormLabel>
                          <FormControl>
                            <Input placeholder={t('sb.placeholderContactName')} {...field} data-testid="input-contact-name" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="contactEmail"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('sb.labelContactEmail')}</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder={t('sb.placeholderContactEmail')} {...field} data-testid="input-contact-email" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={form.control}
                    name="contactPhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('sb.labelContactPhone')}</FormLabel>
                        <FormControl>
                          <Input type="tel" placeholder={t('sb.placeholderContactPhone')} {...field} data-testid="input-contact-phone" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="industry"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('sb.labelIndustry')}</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-industry">
                                <SelectValue placeholder={t('sb.placeholderIndustry')} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {industryKeys.map((ind) => (
                                <SelectItem key={ind.value} value={ind.value} data-testid={`option-industry-${ind.value}`}>
                                  {t(ind.labelKey)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="companySize"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('sb.labelCompanySize')}</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-company-size">
                                <SelectValue placeholder={t('sb.placeholderCompanySize')} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {companySizeKeys.map((size) => (
                                <SelectItem key={size.value} value={size.value} data-testid={`option-size-${size.value}`}>
                                  {t(size.labelKey)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('sb.labelAddress')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('sb.placeholderAddress')} {...field} data-testid="input-address" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <FormField
                      control={form.control}
                      name="city"
                      render={({ field }) => (
                        <FormItem className="col-span-2 sm:col-span-1">
                          <FormLabel>{t('sb.labelCity')}</FormLabel>
                          <FormControl>
                            <Input placeholder={t('sb.labelCity')} {...field} data-testid="input-city" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="state"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('sb.labelState')}</FormLabel>
                          <FormControl>
                            <Input placeholder="MN" {...field} data-testid="input-state" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="zipCode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('sb.labelZipCode')}</FormLabel>
                          <FormControl>
                            <Input placeholder="55401" {...field} data-testid="input-zip-code" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {currentStep === 2 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    {t('sb.step2CardTitle')}
                  </CardTitle>
                  <CardDescription>{t('sb.step2CardDesc')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <FormField
                    control={form.control}
                    name="contributionModel"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('sb.labelContributionModel')}</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-contribution-model">
                              <SelectValue placeholder={t('sb.placeholderContributionModel')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {contributionModelKeys.map((model) => (
                              <SelectItem key={model.value} value={model.value} data-testid={`option-model-${model.value}`}>
                                {t(model.labelKey)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {contributionModel && (
                    <div className="rounded-md border p-4 bg-muted/50" data-testid="model-description">
                      <p className="text-sm text-muted-foreground">
                        {t(contributionModelKeys.find((m) => m.value === contributionModel)?.descKey ?? '')}
                      </p>
                    </div>
                  )}

                  {(contributionModel === "fixed" || contributionModel === "match") && (
                    <FormField
                      control={form.control}
                      name="monthlyPerEmployee"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('sb.labelMonthlyAmount')}</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                              <Input
                                type="number"
                                min={1}
                                className="pl-9"
                                {...field}
                                data-testid="input-monthly-amount"
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {contributionModel === "match" && (
                    <FormField
                      control={form.control}
                      name="matchPercent"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('sb.labelMatchPercent')}</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min={0}
                              max={100}
                              {...field}
                              data-testid="input-match-percent"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {companySize && (contributionModel === "fixed" || contributionModel === "match") && (
                    <Card className="bg-primary/5 border-primary/20">
                      <CardContent className="pt-4 pb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <DollarSign className="w-4 h-4 text-primary" />
                          <span className="text-sm font-medium">{t('sb.costEstimatorTitle')}</span>
                        </div>
                        <p className="text-sm text-muted-foreground" data-testid="text-cost-estimate">
                          {t('sb.costEstimatorText', { count: employeeCount, amount: monthlyPerEmployee || 0 })}
                          <span className="font-semibold text-foreground">${estimatedCost.toLocaleString()}</span>
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </CardContent>
              </Card>
            )}

            {currentStep === 3 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lock className="w-5 h-5" />
                    {t('sb.step3CardTitle')}
                  </CardTitle>
                  <CardDescription>{t('sb.step3CardDesc')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('sb.labelPassword')}</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder={t('sb.placeholderPassword')} {...field} data-testid="input-password" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('sb.labelConfirmPassword')}</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder={t('sb.placeholderConfirmPassword')} {...field} data-testid="input-confirm-password" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Controller
                    name="termsAgreed"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <div className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                        <Checkbox
                          id="terms"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          onBlur={field.onBlur}
                          ref={field.ref}
                          data-testid="checkbox-terms"
                        />
                        <div className="space-y-1 leading-none">
                          <Label htmlFor="terms" className="cursor-pointer font-normal">
                            <Trans
                              i18nKey="sb.agreeToTerms"
                              components={{
                                termsLink: <a href="/terms" className="text-primary underline" target="_blank" rel="noopener noreferrer" />,
                                privacyLink: <a href="/privacy" className="text-primary underline" target="_blank" rel="noopener noreferrer" />
                              }}
                            />
                          </Label>
                          {fieldState.error && (
                            <p className="text-sm font-medium text-destructive">{fieldState.error.message}</p>
                          )}
                        </div>
                      </div>
                    )}
                  />
                </CardContent>
              </Card>
            )}

            <div className="flex items-center justify-between gap-4 mt-6">
              {currentStep > 1 ? (
                <Button type="button" variant="outline" onClick={handleBack} data-testid="button-back">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('sb.back')}
                </Button>
              ) : (
                <div />
              )}
              {currentStep < 3 ? (
                <Button type="button" onClick={handleNext} data-testid="button-next">
                  {t('sb.next')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button type="submit" disabled={signupMutation.isPending} data-testid="button-submit">
                  {signupMutation.isPending ? t('sb.creatingAccount') : t('sb.createAccount')}
                </Button>
              )}
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
